import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {Avatar} from '@udemy-v2/react-core-components';
import {StarRating} from '@udemy-v2/react-merchandising-components';
import {ShowMore} from '@udemy-v2/react-reveal-components';
import {ShowMoreButton} from './show-more-button.react-component';
import classNames from 'classnames';
import React, {useEffect, useLayoutEffect, useState, useRef, useCallback} from 'react';
import {useInView} from 'react-intersection-observer';

import {Review as ReviewType} from '@/types/review';
import {
    ReviewExpandEvent,
    ReviewImpressionEvent,
} from '@/features/course-landing-page/events/events';
import makeRenderContent from '@/lib/make-render-content';
import styles from './review.module.scss';
import showMoreButtonStyles from './show-more-button.module.scss';
import {ReviewsModalButton} from '../review-modal/reviews-modal-button.react-component';
import {InstructorResponse} from '../instructor-response.react-component';
import {ReviewReport} from '../review-report/review-report.react-component';
import {ReviewFeedback} from './review-feedback/review-feedback.react-component';
import {RelativeDuration, useI18n} from '@udemy-v2/i18n';
import {useCommonData} from '@udemy-v2/common-data';

interface ReviewComponentProps {
    review: ReviewType;
    searchedTerm?: string;
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    className?: string;
    isDesktopLayout?: boolean;
    shouldUseInlineShowMore?: boolean;
    shouldUseDeattachedDropdown?: boolean;
    instructorResponseVisibility?: 'visible' | 'hidden' | 'seo-only';
    onShowMoreModalTriggerClick?: () => void;
    isModal?: boolean;
    isInMobileCarousel?: boolean;
}

export const Review = ({
    review,
    searchedTerm,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    className,
    isDesktopLayout = false,
    shouldUseInlineShowMore = true,
    shouldUseDeattachedDropdown = false,
    instructorResponseVisibility = 'hidden',
    onShowMoreModalTriggerClick,
    isModal = false,
    isInMobileCarousel = false,
}: ReviewComponentProps) => {
    const {gettext} = useI18n();
    const commonData = useCommonData();
    const isOrgUser = !!commonData?.organization;
    const reviewContentRef = useRef<HTMLDivElement | null>(null);
    const reviewContainerElementRef = useRef<HTMLDivElement | null>(null);
    const [shouldShowMoreButton, setShouldShowMoreButton] = useState(false);

    // Use IntersectionObserver to only check overflow when review is visible
    // threshold: 0 = trigger as soon as any pixel is visible
    // triggerOnce: false = continue observing in case review scrolls in/out
    const {ref: intersectionRef, inView} = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    // Combine intersection observer ref with container ref
    const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
            reviewContainerElementRef.current = node;
            intersectionRef(node);
        },
        [intersectionRef],
    );

    // Helper function for rounding (moved before checkOverflow to avoid hoisting issues)
    const round = useCallback((n: number, precision = 5) => {
        return Math.round(n / precision) * precision;
    }, []);

    // Check if content overflows and needs a "Show more" button
    const checkOverflow = useCallback(() => {
        if (inView && reviewContentRef.current) {
            const scrollHeight = round(reviewContentRef.current.scrollHeight);
            const clientHeight = round(reviewContentRef.current.clientHeight);
            const hasOverflow = scrollHeight > clientHeight;

            setShouldShowMoreButton(hasOverflow);
        }
    }, [inView, round]);

    // Run overflow check when review comes into view (before paint to prevent flash)
    // Note: By the time useLayoutEffect runs, refs are already set, so this should work
    useLayoutEffect(() => {
        if (inView) {
            checkOverflow();
        }
    }, [inView, checkOverflow]);

    // Observe container resizes and re-check overflow when dimensions change
    // Only set up observer when review is in view for performance
    useEffect(() => {
        if (!inView) {
            return;
        }

        const container = reviewContainerElementRef.current;
        if (!container) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, [inView, checkOverflow]);

    const trackReviewImpression = () => {
        if (!frontendTrackingId) {
            return;
        }

        const instructorResponses = review.response
            ? [
                  {
                      responseId: Number(review.response.id),
                      instructorTrackingId: review.response.user.tracking_id,
                  },
              ]
            : [];
        publishEvent(
            new ReviewImpressionEvent({
                courseTrackingId: frontendTrackingId,
                reviewId: Number(review.id),
                isFeatured: false,
                instructorResponses,
                reviewableObjectId,
                reviewableObjectType,
            }),
        );
    };

    const handleInlineShowMoreToggle = () => {
        if (frontendTrackingId) {
            publishEvent(
                new ReviewExpandEvent({
                    courseTrackingId: frontendTrackingId,
                    reviewId: Number(review.id),
                    isFeatured: false,
                    reviewableObjectId,
                    reviewableObjectType,
                }),
            );
        }
    };

    const handleShowMoreInModalClick = () => {
        if (onShowMoreModalTriggerClick) {
            onShowMoreModalTriggerClick();
        }
    };

    const renderReviewContent = () => {
        const contentAsHtmlWithParagraphs = makeRenderContent(searchedTerm)(
            review.content ?? '',
            true,
        );
        const contentAsHtmlWithoutParagraphs = makeRenderContent(searchedTerm)(
            review.content ?? '',
            false,
        );

        // Inline expansion: Use ShowMore component for desktop and when explicitly requested
        if (shouldUseInlineShowMore) {
            return (
                <ShowMore
                    collapsedHeight={180}
                    contentClassName="ud-text-md"
                    buttonComponent={ShowMoreButton}
                    hideIcons={true}
                    withGradient={true}
                    onToggle={handleInlineShowMoreToggle}
                >
                    <div dangerouslySetInnerHTML={{__html: contentAsHtmlWithParagraphs}} />
                </ShowMore>
            );
        }

        // Modal expansion: Use CSS truncation with modal trigger button
        return (
            <>
                <div
                    ref={reviewContentRef}
                    className={classNames(
                        'ud-text-md',
                        styles['review-content-with-modal-trigger'],
                        {
                            [styles['with-show-more-button']]: shouldShowMoreButton,
                        },
                    )}
                    dangerouslySetInnerHTML={{
                        __html: contentAsHtmlWithoutParagraphs,
                    }}
                />
                {shouldShowMoreButton && (
                    <ReviewsModalButton
                        frontendTrackingId={frontendTrackingId}
                        reviewableObjectType={reviewableObjectType}
                        reviewableObjectId={reviewableObjectId}
                        label={gettext('Show more')}
                        udStyle="ghost"
                        fullWidth={false}
                        onClick={handleShowMoreInModalClick}
                        className={showMoreButtonStyles.button}
                    />
                )}
            </>
        );
    };

    const renderInstructorResponse = () => {
        if (!review.response?.content || instructorResponseVisibility === 'hidden') {
            return null;
        }

        if (instructorResponseVisibility === 'seo-only') {
            return (
                <div className={styles['instructor-response-seo']}>{review.response.content}</div>
            );
        }

        return (
            <InstructorResponse
                content={review.response.content.split(/\n+/).map((line: string, index: number) => (
                    <p key={index}>{line}</p>
                ))}
                user={review.response.user}
                formattedTimestampSince={review.response.createdAt}
            />
        );
    };

    return (
        <div className={classNames(isDesktopLayout && !isModal && styles['impression-wrapper'])}>
            <TrackImpression trackFunc={trackReviewImpression}>
                <div
                    ref={setRefs}
                    className={classNames(
                        styles['review-container'],
                        styles['review-container-with-radius'],
                        className ?? null,
                        {
                            [styles['review-container-mobile-carousel']]: isInMobileCarousel,
                            [styles['review-desktop-container']]: isDesktopLayout,
                            [styles['review-desktop-inline']]:
                                isDesktopLayout && shouldUseInlineShowMore,
                        },
                    )}
                >
                    <div className={styles['review-heading']}>
                        <div className={styles['review-profile']}>
                            <div className={styles['review-name-and-rating']}>
                                <p className="ud-heading-md">{review.user.name}</p>
                                <div className={styles['rating-container']}>
                                    <StarRating rating={review.rating ?? 0} size="medium" />
                                    <span
                                        className={classNames(
                                            'ud-heading-xs',
                                            styles['time-since'],
                                        )}
                                    >
                                        {<RelativeDuration datetime={review.createdAt} />}
                                    </span>
                                </div>
                            </div>
                            <Avatar
                                className={styles['avatar']}
                                data-purpose="avatar"
                                user={review.user}
                                srcKey="image_50x50"
                                size="medium"
                                alt="NONE"
                            />
                        </div>
                        {isDesktopLayout && !isOrgUser && (
                            <ReviewReport
                                reviewId={Number(review.id)}
                                frontendTrackingId={frontendTrackingId}
                                reviewableObjectType={reviewableObjectType}
                                reviewableObjectId={reviewableObjectId}
                                isFeaturedReview={false}
                                shouldUseDeattachedDropdown={shouldUseDeattachedDropdown}
                                reviewAuthorName={review.user.name}
                            />
                        )}
                    </div>
                    <div className={styles['review-content-body']}>{renderReviewContent()}</div>
                    <div className={styles['review-feedback-and-report-container']}>
                        <ReviewFeedback
                            reviewId={Number(review.id)}
                            frontendTrackingId={frontendTrackingId}
                            reviewableObjectId={reviewableObjectId}
                            reviewableObjectType={reviewableObjectType}
                            isFeaturedReview={false}
                            reviewAuthorName={review.user.name}
                        />
                        {!isDesktopLayout && !isOrgUser && (
                            <ReviewReport
                                reviewId={Number(review.id)}
                                frontendTrackingId={frontendTrackingId}
                                reviewableObjectType={reviewableObjectType}
                                reviewableObjectId={reviewableObjectId}
                                isFeaturedReview={false}
                                placement="top-end"
                                shouldUseDeattachedDropdown={shouldUseDeattachedDropdown}
                                reviewAuthorName={review.user.name}
                            />
                        )}
                    </div>
                    {renderInstructorResponse()}
                </div>
            </TrackImpression>
        </div>
    );
};
